/**
 * Breeding Program / Mating Chart Component
 * Manages breeding pairs, planned litters, and genetic compatibility
 */

import { useState, useEffect } from 'react';
import { 
  Heart, 
  Plus, 
  Calendar,
  AlertTriangle,
  CheckCircle,
  Baby,
  GitBranch,
  Trash2,
  Edit,
  Eye,
  Clock
} from 'lucide-react';
import { Card, Button, Badge, Modal, Select, Input, Alert, Tabs } from '../shared/UIComponents';
import { formatDate, formatRelativeDate, cn } from '../../utils/helpers';
import { useAuth } from '../../hooks/useAuth';
import { 
  getCatsByCattery, 
  getLittersByCattery,
  SA_CAT_BREEDS 
} from '../../firebase/firestore';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where, 
  getDocs,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../../firebase/config';

// Breeding status options
const BREEDING_STATUS = {
  planned: { label: 'Planned', color: 'bg-blue-100 text-blue-700', icon: Calendar },
  confirmed: { label: 'Confirmed', color: 'bg-purple-100 text-purple-700', icon: Heart },
  pregnant: { label: 'Pregnant', color: 'bg-pink-100 text-pink-700', icon: Baby },
  delivered: { label: 'Delivered', color: 'bg-paw-100 text-paw-700', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-700', icon: AlertTriangle }
};

// Gestation period for cats (average 63-65 days)
const GESTATION_DAYS = 65;

export const BreedingProgram = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [cats, setCats] = useState([]);
  const [breedingPairs, setBreedingPairs] = useState([]);
  const [litters, setLitters] = useState([]);
  const [showNewPairModal, setShowNewPairModal] = useState(false);
  const [selectedPair, setSelectedPair] = useState(null);
  const [activeTab, setActiveTab] = useState('active');

  useEffect(() => {
    if (user?.catteryId) {
      loadData();
    }
  }, [user?.catteryId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load cats
      const catsData = await getCatsByCattery(user.catteryId);
      setCats(catsData);
      
      // Load breeding pairs
      const pairsQuery = query(
        collection(db, 'breeding_pairs'),
        where('catteryId', '==', user.catteryId)
      );
      const pairsSnap = await getDocs(pairsQuery);
      const pairsData = pairsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBreedingPairs(pairsData);
      
      // Load litters
      const littersData = await getLittersByCattery(user.catteryId);
      setLitters(littersData);
      
    } catch (error) {
      console.error('Error loading breeding data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter cats by sex
  const maleCats = cats.filter(c => c.sex === 'male' && c.status === 'not_for_sale');
  const femaleCats = cats.filter(c => c.sex === 'female' && c.status === 'not_for_sale');

  // Filter breeding pairs by status
  const activePairs = breedingPairs.filter(p => ['planned', 'confirmed', 'pregnant'].includes(p.status));
  const completedPairs = breedingPairs.filter(p => p.status === 'delivered');
  const cancelledPairs = breedingPairs.filter(p => p.status === 'cancelled');

  const tabs = [
    { id: 'active', label: 'Active Pairings', count: activePairs.length },
    { id: 'completed', label: 'Completed', count: completedPairs.length },
    { id: 'chart', label: 'Mating Chart' }
  ];

  const getFilteredPairs = () => {
    switch (activeTab) {
      case 'active': return activePairs;
      case 'completed': return completedPairs;
      default: return breedingPairs;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-gray-900">Breeding Program</h1>
          <p className="text-gray-500">Manage mating pairs, track pregnancies, and plan litters</p>
        </div>
        <Button onClick={() => setShowNewPairModal(true)}>
          <Plus className="w-4 h-4" />
          New Pairing
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard 
          label="Breeding Males" 
          value={maleCats.length} 
          icon={<span className="text-2xl">♂️</span>}
        />
        <StatCard 
          label="Breeding Females" 
          value={femaleCats.length} 
          icon={<span className="text-2xl">♀️</span>}
        />
        <StatCard 
          label="Active Pairings" 
          value={activePairs.length} 
          icon={<Heart className="w-6 h-6 text-pink-500" />}
        />
        <StatCard 
          label="Litters This Year" 
          value={litters.filter(l => {
            const dob = l.dateOfBirth?.toDate?.() || new Date(l.dateOfBirth);
            return dob.getFullYear() === new Date().getFullYear();
          }).length} 
          icon={<Baby className="w-6 h-6 text-feline-500" />}
        />
      </div>

      {/* Pregnant Alert */}
      {activePairs.filter(p => p.status === 'pregnant').length > 0 && (
        <Alert type="info" title="Upcoming Litters">
          <p className="text-sm">
            You have {activePairs.filter(p => p.status === 'pregnant').length} pregnant queen(s). 
            Check expected due dates below.
          </p>
        </Alert>
      )}

      {/* Tabs */}
      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {/* Content */}
      {activeTab === 'chart' ? (
        <MatingChart 
          males={maleCats} 
          females={femaleCats} 
          pairs={breedingPairs}
          onCreatePair={(sireId, damId) => {
            setSelectedPair({ sireId, damId });
            setShowNewPairModal(true);
          }}
        />
      ) : (
        <BreedingPairsList 
          pairs={getFilteredPairs()}
          cats={cats}
          loading={loading}
          onEdit={(pair) => {
            setSelectedPair(pair);
            setShowNewPairModal(true);
          }}
          onRefresh={loadData}
        />
      )}

      {/* New/Edit Pairing Modal */}
      <BreedingPairModal
        isOpen={showNewPairModal}
        onClose={() => {
          setShowNewPairModal(false);
          setSelectedPair(null);
        }}
        pair={selectedPair}
        males={maleCats}
        females={femaleCats}
        catteryId={user?.catteryId}
        onSuccess={() => {
          setShowNewPairModal(false);
          setSelectedPair(null);
          loadData();
        }}
      />
    </div>
  );
};

// Simple stat card
const StatCard = ({ label, value, icon }) => (
  <Card className="flex items-center gap-4">
    <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
      {icon}
    </div>
    <div>
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
  </Card>
);

// Breeding Pairs List
const BreedingPairsList = ({ pairs, cats, loading, onEdit, onRefresh }) => {
  const getCat = (id) => cats.find(c => c.id === id);

  const updateStatus = async (pairId, newStatus) => {
    try {
      const pairRef = doc(db, 'breeding_pairs', pairId);
      const updateData = { 
        status: newStatus,
        updatedAt: serverTimestamp()
      };
      
      // If marking as pregnant, set expected due date
      if (newStatus === 'pregnant') {
        const dueDate = new Date();
        dueDate.setDate(dueDate.getDate() + GESTATION_DAYS);
        updateData.expectedDueDate = Timestamp.fromDate(dueDate);
      }
      
      await updateDoc(pairRef, updateData);
      onRefresh();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const deletePair = async (pairId) => {
    if (!confirm('Are you sure you want to delete this pairing?')) return;
    
    try {
      await deleteDoc(doc(db, 'breeding_pairs', pairId));
      onRefresh();
    } catch (error) {
      console.error('Error deleting pair:', error);
    }
  };

  if (loading) {
    return (
      <Card>
        <div className="animate-pulse space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-24 bg-gray-100 rounded-lg" />
          ))}
        </div>
      </Card>
    );
  }

  if (pairs.length === 0) {
    return (
      <Card className="text-center py-12">
        <Heart className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Breeding Pairs</h3>
        <p className="text-gray-500">Create your first breeding pairing to get started.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {pairs.map((pair) => {
        const sire = getCat(pair.sireId);
        const dam = getCat(pair.damId);
        const statusConfig = BREEDING_STATUS[pair.status];
        const StatusIcon = statusConfig?.icon || Calendar;

        return (
          <Card key={pair.id} className="hover:shadow-lg transition-shadow">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
              {/* Sire */}
              <div className="flex items-center gap-3 flex-1">
                <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center text-2xl">
                  ♂️
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{sire?.name || 'Unknown'}</p>
                  <p className="text-sm text-gray-500">{sire?.breed}</p>
                  <p className="text-xs text-gray-400">{sire?.ems}</p>
                </div>
              </div>

              {/* Heart connector */}
              <div className="hidden lg:flex items-center justify-center w-12">
                <Heart className="w-6 h-6 text-pink-400" fill="#f472b6" />
              </div>

              {/* Dam */}
              <div className="flex items-center gap-3 flex-1">
                <div className="w-16 h-16 bg-pink-100 rounded-xl flex items-center justify-center text-2xl">
                  ♀️
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{dam?.name || 'Unknown'}</p>
                  <p className="text-sm text-gray-500">{dam?.breed}</p>
                  <p className="text-xs text-gray-400">{dam?.ems}</p>
                </div>
              </div>

              {/* Status & Actions */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 lg:gap-4">
                {/* Status badge */}
                <div className={cn(
                  'flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium',
                  statusConfig?.color
                )}>
                  <StatusIcon className="w-4 h-4" />
                  {statusConfig?.label}
                </div>

                {/* Due date if pregnant */}
                {pair.status === 'pregnant' && pair.expectedDueDate && (
                  <div className="flex items-center gap-1 text-sm text-pink-600">
                    <Clock className="w-4 h-4" />
                    Due: {formatDate(pair.expectedDueDate)}
                  </div>
                )}

                {/* Status change dropdown */}
                <select
                  value={pair.status}
                  onChange={(e) => updateStatus(pair.id, e.target.value)}
                  className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-feline-300"
                >
                  <option value="planned">Planned</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="pregnant">Pregnant</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </select>

                {/* Actions */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => onEdit(pair)}
                    className="p-2 text-gray-400 hover:text-feline-600 hover:bg-feline-50 rounded-lg"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => deletePair(pair.id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Notes */}
            {pair.notes && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-sm text-gray-600">{pair.notes}</p>
              </div>
            )}

            {/* Planned date & expected kittens */}
            <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-4 text-sm text-gray-500">
              {pair.plannedDate && (
                <span>Planned: {formatDate(pair.plannedDate)}</span>
              )}
              {pair.expectedKittens && (
                <span>Expected kittens: {pair.expectedKittens}</span>
              )}
              <span>Created: {formatRelativeDate(pair.createdAt)}</span>
            </div>
          </Card>
        );
      })}
    </div>
  );
};

// Visual Mating Chart
const MatingChart = ({ males, females, pairs, onCreatePair }) => {
  // Check if a pair exists
  const getPairStatus = (sireId, damId) => {
    const pair = pairs.find(p => p.sireId === sireId && p.damId === damId);
    return pair?.status || null;
  };

  if (males.length === 0 || females.length === 0) {
    return (
      <Card className="text-center py-12">
        <GitBranch className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Cannot Create Mating Chart</h3>
        <p className="text-gray-500">
          You need at least one breeding male and one breeding female (marked as "Not for Sale") 
          to create a mating chart.
        </p>
      </Card>
    );
  }

  return (
    <Card className="overflow-x-auto">
      <h3 className="font-semibold text-gray-900 mb-4">Mating Compatibility Chart</h3>
      <p className="text-sm text-gray-500 mb-6">
        Click on a cell to create or view a breeding pairing. Green cells indicate existing pairings.
      </p>
      
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th className="p-3 bg-gray-50 border border-gray-200 text-left font-semibold text-gray-700">
              ♂ Sire / ♀ Dam →
            </th>
            {females.map((dam) => (
              <th key={dam.id} className="p-3 bg-pink-50 border border-gray-200 text-center min-w-[120px]">
                <div className="font-semibold text-gray-900">{dam.name}</div>
                <div className="text-xs text-gray-500">{dam.ems}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {males.map((sire) => (
            <tr key={sire.id}>
              <td className="p-3 bg-blue-50 border border-gray-200 font-medium">
                <div className="font-semibold text-gray-900">{sire.name}</div>
                <div className="text-xs text-gray-500">{sire.ems}</div>
              </td>
              {females.map((dam) => {
                const status = getPairStatus(sire.id, dam.id);
                const statusConfig = status ? BREEDING_STATUS[status] : null;
                
                return (
                  <td 
                    key={`${sire.id}-${dam.id}`} 
                    className={cn(
                      'p-3 border border-gray-200 text-center cursor-pointer transition-colors',
                      status ? statusConfig?.color : 'hover:bg-feline-50'
                    )}
                    onClick={() => onCreatePair(sire.id, dam.id)}
                  >
                    {status ? (
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-xs font-medium">{statusConfig?.label}</span>
                      </div>
                    ) : (
                      <Plus className="w-5 h-5 text-gray-300 mx-auto" />
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
};

// New/Edit Breeding Pair Modal
const BreedingPairModal = ({ isOpen, onClose, pair, males, females, catteryId, onSuccess }) => {
  const [formData, setFormData] = useState({
    sireId: '',
    damId: '',
    status: 'planned',
    plannedDate: '',
    expectedKittens: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const isEditing = pair?.id;

  useEffect(() => {
    if (pair) {
      setFormData({
        sireId: pair.sireId || '',
        damId: pair.damId || '',
        status: pair.status || 'planned',
        plannedDate: pair.plannedDate ? formatDateForInput(pair.plannedDate) : '',
        expectedKittens: pair.expectedKittens || '',
        notes: pair.notes || ''
      });
    } else {
      setFormData({
        sireId: '',
        damId: '',
        status: 'planned',
        plannedDate: '',
        expectedKittens: '',
        notes: ''
      });
    }
  }, [pair]);

  const formatDateForInput = (date) => {
    const d = date?.toDate ? date.toDate() : new Date(date);
    return d.toISOString().split('T')[0];
  };

  const handleSubmit = async () => {
    if (!formData.sireId || !formData.damId) {
      setError('Please select both sire and dam');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const data = {
        sireId: formData.sireId,
        damId: formData.damId,
        catteryId,
        status: formData.status,
        plannedDate: formData.plannedDate ? Timestamp.fromDate(new Date(formData.plannedDate)) : null,
        expectedKittens: formData.expectedKittens ? Number(formData.expectedKittens) : null,
        notes: formData.notes,
        updatedAt: serverTimestamp()
      };

      if (isEditing) {
        await updateDoc(doc(db, 'breeding_pairs', pair.id), data);
      } else {
        data.createdAt = serverTimestamp();
        await addDoc(collection(db, 'breeding_pairs'), data);
      }

      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const maleOptions = males.map(c => ({ value: c.id, label: `${c.name} (${c.ems})` }));
  const femaleOptions = females.map(c => ({ value: c.id, label: `${c.name} (${c.ems})` }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Edit Breeding Pair' : 'New Breeding Pair'}
      size="md"
    >
      <div className="space-y-4">
        {error && (
          <Alert type="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Sire (Male) ♂"
            value={formData.sireId}
            onChange={(e) => setFormData({ ...formData, sireId: e.target.value })}
            options={maleOptions}
            placeholder="Select sire"
          />

          <Select
            label="Dam (Female) ♀"
            value={formData.damId}
            onChange={(e) => setFormData({ ...formData, damId: e.target.value })}
            options={femaleOptions}
            placeholder="Select dam"
          />
        </div>

        <Select
          label="Status"
          value={formData.status}
          onChange={(e) => setFormData({ ...formData, status: e.target.value })}
          options={[
            { value: 'planned', label: 'Planned' },
            { value: 'confirmed', label: 'Confirmed' },
            { value: 'pregnant', label: 'Pregnant' },
            { value: 'delivered', label: 'Delivered' },
            { value: 'cancelled', label: 'Cancelled' }
          ]}
        />

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Planned/Mating Date"
            type="date"
            value={formData.plannedDate}
            onChange={(e) => setFormData({ ...formData, plannedDate: e.target.value })}
          />

          <Input
            label="Expected Kittens"
            type="number"
            value={formData.expectedKittens}
            onChange={(e) => setFormData({ ...formData, expectedKittens: e.target.value })}
            placeholder="e.g., 4"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows={3}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-feline-300 focus:border-feline-500 outline-none"
            placeholder="Any notes about this pairing, genetic considerations, etc."
          />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={submitting}>
            {isEditing ? 'Update Pairing' : 'Create Pairing'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default BreedingProgram;
